import { useEffect, useMemo, useState, type FormEvent } from "react";
import { get, updateStudent, type StudentDetail } from "../api.js";
import { useAsync } from "../useAsync.js";
import { idempotencyKey } from "./api.js";
import {
  lessonBlockTransfer,
  type LessonBlockTransferOperation,
  type LessonBlockTransferPreview,
} from "./lessonBlockTransfer.js";
import { scheduling } from "./scheduling.js";
import {
  staffBooking,
  type LessonBlockDetail,
  type LessonBlockProduct,
  type ProviderService,
} from "./staff.js";

type TransferAttempt = {
  fingerprint: string;
  key: string;
};

const message = (cause: unknown, fallback: string) =>
  cause instanceof Error ? cause.message : fallback;

const stateLabel = (state: LessonBlockTransferOperation["state"]) =>
  state.replaceAll("_", " ");

export function TransferRemainingCredit({
  studentId,
  studentName,
  blocks,
  products,
  onChanged,
}: {
  studentId: string;
  studentName: string;
  blocks: LessonBlockDetail[];
  products: LessonBlockProduct[];
  onChanged: (description: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [sourceBlockId, setSourceBlockId] = useState("");
  const [destinationProviderId, setDestinationProviderId] = useState(0);
  const [destinationProductId, setDestinationProductId] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<LessonBlockTransferPreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<TransferAttempt | null>(null);
  const [lastOperation, setLastOperation] = useState<LessonBlockTransferOperation | null>(null);
  const [primaryTutorName, setPrimaryTutorName] = useState<string | null>(null);

  const tutorQuery = useAsync(() => scheduling.tutors(), []);
  const unresolved = useAsync(
    () => lessonBlockTransfer.unresolved(studentId),
    [studentId],
  );

  const sourceCandidates = useMemo(
    () => blocks.filter((block) => block.balance > 0),
    [blocks],
  );
  const source =
    sourceCandidates.find((block) => block.block.id === sourceBlockId) ??
    sourceCandidates[0] ??
    null;

  useEffect(() => {
    if (source && source.block.id !== sourceBlockId) {
      setSourceBlockId(source.block.id);
    }
  }, [source, sourceBlockId]);

  const sourceProviderId = source?.block.provider_id ?? null;
  const destinationTutors = (tutorQuery.data ?? []).filter(
    (tutor) => tutor.providerId !== sourceProviderId,
  );

  useEffect(() => {
    if (
      destinationProviderId <= 0 ||
      !destinationTutors.some((tutor) => tutor.providerId === destinationProviderId)
    ) {
      setDestinationProviderId(destinationTutors[0]?.providerId ?? 0);
      setDestinationProductId("");
    }
  }, [destinationProviderId, destinationTutors]);

  const servicesQuery = useAsync(
    () =>
      destinationProviderId > 0
        ? staffBooking.providerServices(destinationProviderId)
        : Promise.resolve([]),
    [destinationProviderId],
  );
  const services = servicesQuery.data ?? [];
  const compatibleProducts = products.filter(
    (product) =>
      product.is_active &&
      services.some((service) => service.id === product.amelia_lesson_service_id),
  );
  const destinationProduct =
    compatibleProducts.find((product) => product.id === destinationProductId) ??
    compatibleProducts[0] ??
    null;

  useEffect(() => {
    if (destinationProduct && destinationProduct.id !== destinationProductId) {
      setDestinationProductId(destinationProduct.id);
    }
  }, [destinationProduct, destinationProductId]);

  useEffect(() => {
    setPreview(null);
    setConfirmed(false);
  }, [sourceBlockId, destinationProviderId, destinationProductId]);

  const sourceTutorName =
    (tutorQuery.data ?? []).find((tutor) => tutor.providerId === sourceProviderId)
      ?.displayName ?? (sourceProviderId === null ? "Unknown tutor" : `Tutor ${sourceProviderId}`);
  const destinationTutor = (tutorQuery.data ?? []).find(
    (tutor) => tutor.providerId === destinationProviderId,
  );
  const destinationService = destinationProduct
    ? services.find((service) => service.id === destinationProduct.amelia_lesson_service_id) ?? null
    : null;

  async function loadPreview(event: FormEvent) {
    event.preventDefault();
    if (!source || !destinationProduct || destinationProviderId <= 0) return;
    setBusy(true);
    setProblem(null);
    setConfirmed(false);
    try {
      const next = await lessonBlockTransfer.preview(
        source.block.id,
        destinationProviderId,
        destinationProduct.id,
      );
      setPreview(next);
    } catch (cause) {
      setProblem(message(cause, "Could not preview that credit transfer."));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function applyTransfer() {
    if (!source || !destinationProduct || !preview || !confirmed) return;
    const trimmedReason = reason.trim();

    const fingerprint = JSON.stringify({
      source: source.block.id,
      destinationProviderId,
      destinationProductId: destinationProduct.id,
      previewToken: preview.preview_token,
      reason: trimmedReason,
    });
    const nextAttempt =
      attempt?.fingerprint === fingerprint
        ? attempt
        : { fingerprint, key: idempotencyKey() };
    setAttempt(nextAttempt);
    setBusy(true);
    setProblem(null);
    try {
      const operation = await lessonBlockTransfer.apply(
        source.block.id,
        {
          destination_provider_id: destinationProviderId,
          destination_product_id: destinationProduct.id,
          preview_token: preview.preview_token,
          reason: trimmedReason,
        },
        nextAttempt.key,
      );
      setLastOperation(operation);
      await handleOperation(operation);
    } catch (cause) {
      setProblem(message(cause, "Could not apply that credit transfer."));
    } finally {
      setBusy(false);
    }
  }

  async function refreshPrimaryTutor(): Promise<string | null> {
    const detail = await get<StudentDetail>(
      `/admin/students/${encodeURIComponent(studentId)}`,
    );
    setPrimaryTutorName(detail.student.tutorName);
    return detail.student.tutorName;
  }

  async function finishHubAssignment(
    operation: LessonBlockTransferOperation,
  ): Promise<void> {
    const tutor = (tutorQuery.data ?? []).find(
      (candidate) => candidate.providerId === operation.destination_provider_id,
    );
    if (!tutor) {
      throw new Error(
        `Transfer ${operation.id} is durable, but provider ${operation.destination_provider_id} no longer resolves to an active Hub tutor. The Hub assignment needs reconciliation.`,
      );
    }

    try {
      await updateStudent(studentId, { tutorId: tutor.tutorProfileId });
    } catch (cause) {
      const detail = message(cause, "The Hub tutor assignment failed.");
      try {
        const failed = await lessonBlockTransfer.recordHubReconciliation(
          operation.id,
          false,
          detail,
        );
        setLastOperation(failed);
      } catch {
        // The durable FastAPI operation is still discoverable as pending even
        // if recording the Hub-side failure is itself unavailable.
      }
      throw new Error(
        `Credit transfer ${operation.id} is durable, but the Hub primary tutor was not changed. Retry the reconciliation below. ${detail}`,
      );
    }

    let completed: LessonBlockTransferOperation;
    try {
      completed = await lessonBlockTransfer.recordHubReconciliation(
        operation.id,
        true,
      );
    } catch (cause) {
      const refreshed = await refreshPrimaryTutor().catch(() => tutor.displayName);
      throw new Error(
        `Primary tutor is now ${refreshed ?? tutor.displayName}, but FastAPI could not record the Hub acknowledgement for transfer ${operation.id}. Retry reconciliation; do not transfer the credit again. ${message(cause, "")}`.trim(),
      );
    }

    setLastOperation(completed);
    const refreshed = await refreshPrimaryTutor().catch(() => tutor.displayName);
    await onChanged(
      `${operation.transferred_credits} credits transferred. Operation ${operation.id} completed; primary tutor is ${refreshed ?? tutor.displayName}.`,
    );
    await unresolved.reload();
    setOpen(false);
    setPreview(null);
    setConfirmed(false);
  }

  async function handleOperation(
    operation: LessonBlockTransferOperation,
  ): Promise<void> {
    if (operation.state === "completed") {
      await refreshPrimaryTutor().catch(() => null);
      await onChanged(
        `${operation.transferred_credits} credits transferred. Operation ${operation.id} is complete.`,
      );
      await unresolved.reload();
      setOpen(false);
      return;
    }

    if (
      operation.state === "hub_assignment_pending" ||
      operation.state === "hub_assignment_failed"
    ) {
      await finishHubAssignment(operation);
      return;
    }

    // FastAPI commits the transfer ledger before coupon reconciliation. Refresh
    // the credit panel now so it never continues showing the pre-transfer
    // balance while the durable operation waits for reconciliation.
    await onChanged(
      `${operation.transferred_credits} credits moved under operation ${operation.id}, but reconciliation is still ${stateLabel(operation.state)}.`,
    );
    await unresolved.reload();
    setProblem(
      `Credit truth has already moved under operation ${operation.id}. Do not start another transfer. Resolve the reconciliation item below.`,
    );
  }

  async function retryOperation(operation: LessonBlockTransferOperation) {
    setBusy(true);
    setProblem(null);
    try {
      const next =
        operation.state === "requires_attention" ||
        operation.state === "coupon_reconciliation_pending"
          ? await lessonBlockTransfer.retry(operation.id)
          : operation;
      setLastOperation(next);
      await handleOperation(next);
    } catch (cause) {
      setProblem(message(cause, `Could not reconcile transfer ${operation.id}.`));
      unresolved.reload();
    } finally {
      setBusy(false);
    }
  }

  const unresolvedItems = unresolved.data ?? [];

  return (
    <div className="credit-transfer-control">
      {unresolvedItems.length > 0 && (
        <div className="notice" aria-label="Credit transfer reconciliation">
          <strong>Credit transfer reconciliation</strong>
          <p className="small">
            These transfers already have durable FastAPI operation ids. Reconcile
            them here; do not create replacement transfers.
          </p>
          {unresolvedItems.map((operation) => {
            const tutor = (tutorQuery.data ?? []).find(
              (candidate) => candidate.providerId === operation.destination_provider_id,
            );
            const product = products.find(
              (candidate) => candidate.id === operation.destination_product_id,
            );
            return (
              <div className="small" key={operation.id} style={{ marginTop: 10 }}>
                <strong>{operation.transferred_credits} credits</strong> · provider{" "}
                {operation.source_provider_id} → {tutor?.displayName ?? `provider ${operation.destination_provider_id}`}
                {product ? ` · ${product.display_name}` : ""}
                <div className="muted">
                  Operation {operation.id} · {stateLabel(operation.state)}
                </div>
                {operation.last_error && (
                  <div className="muted">Last error: {operation.last_error}</div>
                )}
                <button
                  type="button"
                  disabled={busy || tutorQuery.loading}
                  onClick={() => void retryOperation(operation)}
                  style={{ marginTop: 6 }}
                >
                  {operation.state === "requires_attention" ||
                  operation.state === "coupon_reconciliation_pending"
                    ? "Retry transfer reconciliation"
                    : "Retry primary tutor update"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!open ? (
        <button className="action-tertiary" type="button" onClick={() => setOpen(true)}>
          Transfer remaining credits
        </button>
      ) : (
        <form className="settings-plan" onSubmit={(event) => void loadPreview(event)}>
          <div className="spread" style={{ alignItems: "baseline" }}>
            <div>
              <strong>Transfer remaining credits</strong>
              <div className="small muted">Owner/admin cross-provider correction</div>
            </div>
            <button type="button" className="linklike" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>

          {problem && <div className="error small">{problem}</div>}
          {unresolved.error && (
            <div className="error small">
              Existing transfer reconciliation state could not be loaded: {unresolved.error}
            </div>
          )}

          <label>
            <span className="small muted">Source entitlement</span>
            <select
              value={source?.block.id ?? ""}
              onChange={(event) => setSourceBlockId(event.target.value)}
              disabled={sourceCandidates.length === 0}
              required
            >
              {sourceCandidates.map((block) => (
                <option value={block.block.id} key={block.block.id}>
                  {block.product.display_name} · provider {block.block.provider_id} · {block.balance} remaining
                </option>
              ))}
            </select>
          </label>
          {source ? (
            <p className="small">
              <span className="muted">Source: </span>
              {sourceTutorName} · {source.product.display_name} · {source.balance} remaining from{" "}
              {source.block.credits_purchased} original entitlement
            </p>
          ) : (
            <p className="small muted">No entitlement with a positive ledger balance is available.</p>
          )}

          <label>
            <span className="small muted">Destination tutor</span>
            <select
              value={destinationProviderId || ""}
              onChange={(event) => {
                setDestinationProviderId(Number(event.target.value));
                setDestinationProductId("");
              }}
              disabled={tutorQuery.loading || destinationTutors.length === 0}
              required
            >
              {destinationTutors.map((tutor) => (
                <option value={tutor.providerId} key={tutor.tutorProfileId}>
                  {tutor.displayName}
                </option>
              ))}
            </select>
          </label>
          {tutorQuery.error && <div className="error small">{tutorQuery.error}</div>}

          <label>
            <span className="small muted">Destination compatible service</span>
            <select
              value={destinationProduct?.id ?? ""}
              onChange={(event) => setDestinationProductId(event.target.value)}
              disabled={servicesQuery.loading || compatibleProducts.length === 0}
              required
            >
              {compatibleProducts.map((product) => {
                const service = services.find(
                  (candidate) => candidate.id === product.amelia_lesson_service_id,
                );
                return (
                  <option value={product.id} key={product.id}>
                    {service?.name ?? product.display_name}
                  </option>
                );
              })}
            </select>
          </label>
          {servicesQuery.error && <div className="error small">{servicesQuery.error}</div>}
          {!servicesQuery.loading && compatibleProducts.length === 0 && destinationProviderId > 0 && (
            <p className="small muted">No configured lesson-block product matches this tutor&rsquo;s services.</p>
          )}

          <button
            type="submit"
            disabled={busy || !source || !destinationProduct || destinationProviderId <= 0}
          >
            {busy ? "Checking…" : "Preview transfer"}
          </button>

          {preview && (
            <TransferPreview
              preview={preview}
              source={source}
              destinationTutorName={destinationTutor?.displayName ?? `Tutor ${destinationProviderId}`}
              destinationService={destinationService}
              destinationProduct={destinationProduct}
              reason={reason}
              setReason={setReason}
              confirmed={confirmed}
              setConfirmed={setConfirmed}
              busy={busy}
              onApply={() => void applyTransfer()}
            />
          )}

          {lastOperation && lastOperation.state !== "completed" && (
            <p className="small muted">
              Latest operation: {lastOperation.id} · {stateLabel(lastOperation.state)}
            </p>
          )}
          {primaryTutorName && (
            <p className="small muted">Current Hub primary tutor: {primaryTutorName}</p>
          )}
        </form>
      )}
    </div>
  );
}

function TransferPreview({
  preview,
  source,
  destinationTutorName,
  destinationService,
  destinationProduct,
  reason,
  setReason,
  confirmed,
  setConfirmed,
  busy,
  onApply,
}: {
  preview: LessonBlockTransferPreview;
  source: LessonBlockDetail | null;
  destinationTutorName: string;
  destinationService: ProviderService | null;
  destinationProduct: LessonBlockProduct | null;
  reason: string;
  setReason: (value: string) => void;
  confirmed: boolean;
  setConfirmed: (value: boolean) => void;
  busy: boolean;
  onApply: () => void;
}) {
  return (
    <div className="notice" style={{ marginTop: 12 }}>
      <strong>Confirm transfer</strong>
      <table style={{ marginTop: 8 }}>
        <tbody>
          <tr>
            <td className="small muted">Student</td>
            <td>{preview.student_name ?? preview.portal_student_id}</td>
          </tr>
          <tr>
            <td className="small muted">Source</td>
            <td>
              Provider {preview.source_provider_id} · {source?.product.display_name ?? `service ${preview.source_service_id}`}
            </td>
          </tr>
          <tr>
            <td className="small muted">Destination</td>
            <td>
              {destinationTutorName} · {destinationService?.name ?? destinationProduct?.display_name ?? `service ${preview.destination_service_id}`}
            </td>
          </tr>
          <tr>
            <td className="small muted">Original entitlement</td>
            <td>{source?.block.credits_purchased ?? "—"} credits</td>
          </tr>
          <tr>
            <td className="small muted">Exact unused amount moving</td>
            <td>
              <strong>{preview.transferred_credits} credits</strong>
            </td>
          </tr>
          <tr>
            <td className="small muted">Source classification</td>
            <td>{preview.source_classification.replaceAll("_", " ")}</td>
          </tr>
        </tbody>
      </table>

      <p className="small">{preview.history_statement}</p>
      <ul className="small">
        {preview.coupon_consequences.map((consequence) => (
          <li key={consequence}>{consequence}</li>
        ))}
      </ul>
      <p className="small">
        <strong>Already-booked lessons are unchanged.</strong>{" "}
        {preview.future_appointments_statement}
      </p>

      {preview.blockers.length > 0 && (
        <div className="error small">
          <strong>Cannot apply this preview:</strong>
          <ul>
            {preview.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      )}

      <label>
        <span className="small muted">Note (optional)</span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          maxLength={2000}
        />
      </label>
      <label className="row small" style={{ alignItems: "flex-start" }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <span>
          I understand this creates a new destination entitlement and an append-only linked transfer trail. It does not rewrite the old block, payment, taught lessons, future appointments or recurrence plan.
        </span>
      </label>
      <button
        type="button"
        disabled={!preview.can_apply || !confirmed || busy}
        onClick={onApply}
      >
        {busy ? "Applying…" : `Transfer exactly ${preview.transferred_credits} credits`}
      </button>
    </div>
  );
}
